import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Box, Card, CircularProgress, IconButton, IconButtonProps, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { useConfig } from "../../contexts/ConfigContext";
import { useSearch } from "../../hooks/useSearch";
import { MediaItemDetailed, Entity } from "../../types";
import { getMediaThumbnailSync, getMediaThumbnail, getMediaFull } from "../../utilities/authMediaAdapters";
import { getThumbnailIcon } from "../../utilities/mediaUtils";
import { getSafeThumbnailUrl } from "../../utilities/mediaUtils";
import { getRelatedMedia, getDataSourceType } from "../../utilities/dataSourceAdapters";
import ErrorBoundary from "../common/ErrorBoundary";
import SearchBar from "../common/search-bar";
import ToggleButton from "../common/ToggleButton";
import { useAuthSelector } from "../../store/auth";
import { useUIActions, useUISelector } from "../../store/UI";
import { useMediaActions, useMediaSelector } from "../../store/media";
import { useSpinner } from "../../contexts/SpinnerContext";
import { IUserResponse } from "../../api/auth";
import Image from "../common/Image";
import { ChevronLeft, ChevronRight, Fullscreen, FullscreenExit, ZoomIn, ZoomOut } from "@mui/icons-material";
import { NAVBAR_HEIGHT } from "../../pages/Map";
import { useSelectionActions, useSelectionSelector } from "../../store/selection";
import { usePopupActions } from "../../store/popupStore";
import { hasValidGeometry } from "../../utilities/geoUtils";
import { entityIsMedia } from "../../utilities/entityModalUtils";
import { useSelectedEntity, useMediaDrawerRelationshipGate } from "../../hooks/useSelectedEntity";
import { useSnackbar } from "notistack";
import { enqueueUniqueSnackbar } from "../../utilities/snackbarUtils";
import { useLogout } from "../../hooks/useLogout";
import { apiFetch } from "../../utilities/api";

// This component is replicated from src/components/media/MediaDrawer.tsx with enhancements for the new media drawer UI.
// @todo: Eventually we should refactor to have a single MediaDrawer component with props for different modes.
// For now, keeping them separate for easier testing and iteration.

// Feature flags
const SHOW_SEARCH_BAR = false; // Toggle search functionality

const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 3000 },
    items: 6,
    slidesToSlide: 6
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 6,
    slidesToSlide: 6
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 4,
    slidesToSlide: 4
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 2,
    slidesToSlide: 2
  }
};

const MAX_ITEMS = 50; // Limit the number of items shown
export const MEDIA_DRAWER_CONTENT_HEIGHT = 121;

// Fullscreen grid tile size (px). User can adjust between MIN and MAX in STEP
// increments via the zoom-in / zoom-out buttons in the drawer toolbar.
const GRID_TILE_SIZE_DEFAULT = 180;
const GRID_TILE_SIZE_MIN = 120;
const GRID_TILE_SIZE_MAX = 360;
const GRID_TILE_SIZE_STEP = 40;

// Enhanced thumbnail URL generation using media adapter pattern
const generateThumbnailUrl = async (item: MediaItemDetailed, userProfile: any, mediaEndpoint?: any): Promise<string> => {
  console.log("[MediaDrawer] 🖼️ Generating thumbnail for item using adapter pattern:", {
    id: item.id,
    name: item.name,
    dataSourceType: getDataSourceType(item)
  });

  try {
    // Prefer the same sync thumbnail logic used by DataGrid cards, but only when it's "safe".
    // For non-Django sources (e.g. Fulcrum), thumbnail URLs can require auth headers and will break
    // when used as plain <img src>. In those cases, rely on the async media adapters instead.
    const dataSourceType = getDataSourceType(item);
    if (dataSourceType === "django") {
      // This ensures Django media uses `/api/user/media/{id}/thumbnail/` and avoids guesswork.
      const safeThumb = getSafeThumbnailUrl(item, mediaEndpoint);
      if (safeThumb) {
        return safeThumb;
      }
    }

    // Use the media adapter - handles all data sources uniformly
    const thumbnailResult = await getMediaThumbnail(item, userProfile);
    console.log("[MediaDrawer] ✅ Got thumbnail from adapter:", {
      authMethod: thumbnailResult.authMethod,
      hasUrl: !!thumbnailResult.url,
      requiresBlobConversion: thumbnailResult.requiresBlobConversion
    });
    return thumbnailResult.url;
  } catch (error) {
    console.error("[MediaDrawer] ❌ Adapter failed, falling back to sync method:", error);

    // Fallback to sync method
    const thumbnailUrl = getMediaThumbnailSync(item);
    if (thumbnailUrl) {
      console.log("[MediaDrawer] 📎 Using sync thumbnail URL:", thumbnailUrl);
      return thumbnailUrl;
    }

    // Final fallback to icon
    const fallbackIcon = getThumbnailIcon(item.url || item.remote_details?.url || "");
    console.log("[MediaDrawer] 🎭 Using fallback icon:", fallbackIcon);
    return fallbackIcon;
  }
};

export function updateMediaList(items: MediaItemDetailed[], newItem: MediaItemDetailed) {
  const newItemKey = `${newItem?.type}-${newItem?.id}`;

  return items.map((item) => {
    const itemKey = `${item?.type}-${item?.id}`;
    return itemKey === newItemKey ? newItem : item;
  });
}

// Fetch entity url and thumbnail
export async function fetchMediaUrlAndThumbnail(item: MediaItemDetailed, profile?: IUserResponse | null) {
  console.log("[fetchMediaUrlAndThumbnail] 🔄 Using media adapters to prepare item for preview...");

  const [thumbnailResult, fullImageResult] = await Promise.all([getMediaThumbnail(item, profile), getMediaFull(item, profile)]);

  console.log("[fetchMediaUrlAndThumbnail] 📸 Media adapter results:", {
    thumbnail: {
      authMethod: thumbnailResult.authMethod,
      hasUrl: !!thumbnailResult.url,
      requiresBlobConversion: thumbnailResult.requiresBlobConversion
    },
    fullImage: {
      authMethod: fullImageResult.authMethod,
      hasUrl: !!fullImageResult.url,
      requiresBlobConversion: fullImageResult.requiresBlobConversion
    }
  });

  console.log("[fetchMediaUrlAndThumbnail] ✅ Successfully prepared item for preview using adapter pattern");
  const mediaType = (item.media_type || (item as any).type || "").toString().toLowerCase();
  const isEmbed = mediaType.startsWith("embed/") || mediaType === "embed";

  // For embeds, the viewer needs embed_url (iframe src). If it's missing, fetch full details.
  let embedUrl = (item as any).embed_url || (item as any)?.remote_details?.embed_url;
  let remoteDetails = (item as any)?.remote_details;
  if (typeof remoteDetails === "string") {
    try {
      remoteDetails = JSON.parse(remoteDetails);
    } catch {
      // ignore
    }
  }
  embedUrl = embedUrl || remoteDetails?.embed_url;

  if (isEmbed && !embedUrl && typeof item.id === "number") {
    try {
      const fetched = await apiFetch(`api/v2/media/${item.id}/`);
      const fetchedRemote = typeof fetched?.remote_details === "string" ? JSON.parse(fetched.remote_details) : fetched?.remote_details;
      embedUrl = fetched?.embed_url || fetchedRemote?.embed_url || embedUrl;
      remoteDetails = fetchedRemote || remoteDetails;
    } catch (e) {
      // leave embedUrl empty; viewer will show a helpful fallback
    }
  }

  // Combine item with adapter-provided URLs.
  // Important: for embeds, keep `url` as the real media URL if present (or empty),
  // but always populate `embed_url` so MediaViewer can render the iframe.
  return {
    ...item,
    url: isEmbed ? item?.url || "" : fullImageResult.url || item?.url,
    thumbnail: thumbnailResult.url || item.thumbnail,
    embed_url: isEmbed ? embedUrl || fullImageResult.url || "" : (item as any).embed_url,
    remote_details: remoteDetails || (item as any).remote_details
  };
}

interface MediaDrawerProps {
  /**
   * When the timeline is open, offset the expanded (fullscreen) drawer below it
   * so the timeline does not overlap the drawer content. 0 = full height.
   */
  timelineOffsetPx?: number;
}

const MediaDrawer: React.FC<MediaDrawerProps> = ({ timelineOffsetPx = 0 }) => {
  // Get all data from context - pure reactive component
  // const { selectedEntity, setSelectedEntity, selectedEndpoint } = useContext(SelectionContext);
  const { entity: selectedEntity } = useSelectedEntity();
  const { selectedEndpoint } = useSelectionSelector();
  // Opens with the relationship panel, or automatically under alwaysRequestRelatedItemsAndMedia.
  const mediaDrawerRelationshipGate = useMediaDrawerRelationshipGate();
  const { setSelectedPrimaryStates } = useSelectionActions();
  const { openGeoPopup, openMediaViewer, openNonGeoPopup } = usePopupActions();
  const config = useConfig();
  const theme = useTheme();
  const isCompactMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompactTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const compactVisibleSlots = isCompactMobile ? 2 : isCompactTablet ? 4 : 6;
  const { user: profile } = useAuthSelector();
  const mediaEndpoint = useMemo(() => config.apiEndpoints.find((ep) => ep.entityType === "media"), [config.apiEndpoints]);

  const { isMediaDrawerVisible, mediaDrawerManuallyClosedByUser, dataDrawerMode } = useUISelector();
  const { setMediaDrawerManualCloseByUser, setMediaDrawerVisibility, setDataDrawerMode } = useUIActions();
  const isDrawerExpanded = dataDrawerMode === "fullscreen";
  const { selectedItem, items } = useMediaSelector();
  const { setMediaItems, setCommonMediaState, setSelectedItem, openMediaDetail } = useMediaActions();
  const { startLoading, stopLoading } = useSpinner();
  const { enqueueSnackbar } = useSnackbar();
  const { logout } = useLogout();

  // PERFORMANCE MONITORING: Track what causes thumbnail cache rebuilds
  const cacheRebuildCountRef = useRef(0);
  const lastEntityIdRef = useRef<number | string | null>(null);
  const lastMediaCountRef = useRef<number>(0);
  const entityIdRef = useRef<number | string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Guard against redundant setMediaItems calls (prevents infinite loop)
  // const lastMediaSignatureRef = useRef<string>("");

  // Determine if we should show the toggle button (always show when entity is selected)
  const shouldShowToggleButton = useMemo(() => {
    // Show toggle button whenever an entity is selected, regardless of DataDrawer mode
    return !!selectedEntity;
  }, [selectedEntity]);

  // Extract media items using an adapter for consistent access across data sources.
  useEffect(() => {
    if (!selectedEntity) {
      setCommonMediaState({
        items: [],
        selectedItem: undefined
      });
      return;
    }

    // Reassign media when the relationship panel is active (manual or config-driven).
    if (!mediaDrawerRelationshipGate && !items) return;

    // Use adapter function for consistent media extraction across data sources
    const relatedItems = getRelatedMedia(selectedEntity);

    // Update store media list with extracted data
    setMediaItems(relatedItems);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity, setCommonMediaState, setMediaItems, mediaDrawerRelationshipGate]);

  const displayMediaItems = useMemo(() => items || [], [items]);

  // PERFORMANCE: Track stable media signature to prevent unnecessary cache rebuilds
  const mediaSignatureRef = useRef<string>("");
  const lastCacheRef = useRef<Map<string, Promise<string>>>(new Map());

  // OPTIMIZED: Create a truly stable thumbnail cache using ref-based signature comparison
  // Now handles async thumbnail generation for Fulcrum photos
  const thumbnailCache = useMemo(() => {
    const currentEntityId = selectedEntity?.id || null;
    const currentMediaCount = displayMediaItems.length;

    // Create current signature
    // Include thumbnail-ish fields in the signature so the cache refreshes when a media item
    // gains a server thumbnail after detail fetch (common for non-image media like 3D scans).
    const currentSignature = `${currentEntityId}-${currentMediaCount}-${displayMediaItems
      .map((item) => {
        const thumb = (item as any).entity_thumbnail || item.thumbnail || item.remote_details?.thumbnail || "";
        return `${item.id}:${thumb}`;
      })
      .sort()
      .join(",")}`;

    // Only rebuild if signature actually changed
    if (mediaSignatureRef.current === currentSignature && lastCacheRef.current.size > 0) {
      console.log(`🔍 [MediaDrawer] PERF: Cache signature unchanged, reusing existing cache (${lastCacheRef.current.size} entries)`);
      return lastCacheRef.current;
    }

    // Signature changed - rebuild cache
    cacheRebuildCountRef.current++;
    mediaSignatureRef.current = currentSignature;

    console.log(`🔍 [MediaDrawer] PERF: Thumbnail cache rebuild #${cacheRebuildCountRef.current}:`, {
      rebuildCount: cacheRebuildCountRef.current,
      entityId: currentEntityId,
      mediaCount: currentMediaCount,
      entityChanged: lastEntityIdRef.current !== currentEntityId,
      mediaCountChanged: lastMediaCountRef.current !== currentMediaCount,
      signatureChanged: true,
      currentSignature: currentSignature,
      prevEntityId: lastEntityIdRef.current,
      prevMediaCount: lastMediaCountRef.current
    });

    lastEntityIdRef.current = currentEntityId;
    lastMediaCountRef.current = currentMediaCount;

    console.log("[MediaDrawer] Building async thumbnail cache for", displayMediaItems.length, "items");
    const cache = new Map<string, Promise<string>>();

    displayMediaItems.forEach((item) => {
      const itemKey = item.id.toString();
      if (!cache.has(itemKey)) {
        // Store the promise in cache for async thumbnail generation
        const thumbnailPromise = generateThumbnailUrl(item, profile, mediaEndpoint);
        cache.set(itemKey, thumbnailPromise);
      }
    });

    console.log("[MediaDrawer] Async thumbnail cache built with", cache.size, "entries");
    lastCacheRef.current = cache;
    return cache;
  }, [
    selectedEntity?.id,
    displayMediaItems.length,
    // Include thumb-ish fields so cache can rebuild when details arrive.
    displayMediaItems
      .map((item) => {
        // Normalize remote_details which can arrive as a JSON string in some API responses
        let remoteDetails: any = (item as any).remote_details;
        if (typeof remoteDetails === "string") {
          try {
            remoteDetails = JSON.parse(remoteDetails);
          } catch {
            // ignore parse failures
          }
        }
        const remoteThumb = remoteDetails?.thumbnail;
        const thumb = (item as any).entity_thumbnail || item.thumbnail || remoteThumb || "";
        return `${item.id}:${thumb}`;
      })
      .join(","),
    mediaEndpoint?.tableName
  ]); // Intentional - let useMemo run, but we control rebuilding internally

  // State for tracking thumbnail loading status
  const [thumbnailStates, setThumbnailStates] = useState<Map<string, { url: string; loading: boolean; error: boolean }>>(new Map());

  // Effect to resolve thumbnail promises and update states
  useEffect(() => {
    const resolvePromises = async () => {
      const newStates = new Map<string, { url: string; loading: boolean; error: boolean }>();

      // Initialize loading states
      displayMediaItems.forEach((item) => {
        const itemKey = item.id.toString();
        newStates.set(itemKey, { url: "", loading: true, error: false });
      });
      setThumbnailStates(newStates);

      // Resolve all thumbnail promises
      const promises = displayMediaItems.map(async (item) => {
        const itemKey = item.id.toString();
        const thumbnailPromise = thumbnailCache.get(itemKey);

        if (thumbnailPromise) {
          try {
            const url = await thumbnailPromise;
            setThumbnailStates((prev) => {
              const updated = new Map(prev);
              updated.set(itemKey, { url, loading: false, error: false });
              return updated;
            });
          } catch (error) {
            console.error(`[MediaDrawer] Failed to load thumbnail for ${itemKey}:`, error);
            const fallbackUrl = getThumbnailIcon("");
            setThumbnailStates((prev) => {
              const updated = new Map(prev);
              updated.set(itemKey, { url: fallbackUrl, loading: false, error: true });
              return updated;
            });
          }
        }
      });

      await Promise.all(promises);
    };

    if (displayMediaItems.length > 0) {
      resolvePromises();
    }
  }, [thumbnailCache, displayMediaItems]);

  // OPTIMIZED: Create a stable thumbnail URL getter that uses the resolved states
  const getThumbnailUrl = useCallback(
    (item: MediaItemDetailed): { url: string; loading: boolean; error: boolean } => {
      const itemKey = item.id.toString();
      const state = thumbnailStates.get(itemKey);

      if (state) {
        return state;
      }

      // Fallback if somehow not found
      console.warn("[MediaDrawer] Item not found in thumbnail states, using fallback:", item.id);
      return {
        url: getThumbnailIcon(""),
        loading: false,
        error: true
      };
    },
    [thumbnailStates]
  );

  const { searchTerm, setSearchTerm, filteredItems } = useSearch(displayMediaItems);

  // Compact (carousel) mode keeps the MAX_ITEMS cap for performance / aesthetics;
  // fullscreen mode lets the user browse the entire set.
  const limitedItems = useMemo(
    () => (isDrawerExpanded ? filteredItems : filteredItems.slice(0, MAX_ITEMS)),
    [filteredItems, isDrawerExpanded]
  );
  const shouldCenterCompactItems = !isDrawerExpanded && limitedItems.length > 0 && limitedItems.length < compactVisibleSlots;

  // Show a message if there are more items than we're displaying
  // const hasMoreItems = filteredItems.length > MAX_ITEMS;

  const toggleDrawer = useCallback(() => {
    setMediaDrawerVisibility(!isMediaDrawerVisible);
  }, [setMediaDrawerVisibility, isMediaDrawerVisible]);

  const toggleDrawerExpansion = useCallback(() => {
    setDataDrawerMode(isDrawerExpanded ? "compact" : "fullscreen");
  }, [setDataDrawerMode, isDrawerExpanded]);

  // Grid tile size (px) — only meaningful in fullscreen mode. Local state, resets
  // on entity change via the existing entity-change effect (no separate reset needed
  // because the component remounts only on full mode reset).
  const [gridTileSize, setGridTileSize] = useState<number>(GRID_TILE_SIZE_DEFAULT);
  const zoomGridIn = useCallback(() => {
    setGridTileSize((s) => Math.min(GRID_TILE_SIZE_MAX, s + GRID_TILE_SIZE_STEP));
  }, []);
  const zoomGridOut = useCallback(() => {
    setGridTileSize((s) => Math.max(GRID_TILE_SIZE_MIN, s - GRID_TILE_SIZE_STEP));
  }, []);
  const canZoomIn = gridTileSize < GRID_TILE_SIZE_MAX;
  const canZoomOut = gridTileSize > GRID_TILE_SIZE_MIN;

  // Constants for consistent styling
  // Toggle-button assembly only renders in compact mode so a single z-index
  // suffices. Drawer panel z-index is the same in both modes today, but the
  // separate constants are kept to make a future split (e.g. raising the
  // expanded panel above other map overlays) cheaper.
  const Z_INDEX_TOGGLE_COMPACT = 999;
  const Z_INDEX_DRAWER_FULLSCREEN = 99;
  const Z_INDEX_DRAWER_COMPACT = 99;

  // Smart effect to manage MediaDrawer visibility - immediate action for complete entities, debounced for others
  useEffect(() => {
    if (!selectedEntity) {
      // Always hide when no entity
      if (isMediaDrawerVisible) {
        setMediaDrawerVisibility(false);
      }
      return;
    }

    const hasMedia = displayMediaItems?.length > 0;
    if (hasMedia) {
      setMediaDrawerVisibility(true);
      return;
    }

    // Skip ALL auto-show logic if user recently manually closed (prevent render loop)
    if (mediaDrawerManuallyClosedByUser) {
      return;
    }

    // IMMEDIATE ACTION: If entity is complete and has media, open immediately (don't debounce)
    if (selectedEntity.isComplete === true && hasMedia && !isMediaDrawerVisible) {
      setMediaDrawerVisibility(true);
      return; // Don't set timeout for this case
    }

    // Clear any existing timeout to prevent stale callbacks
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // DEBOUNCED ACTION: For all other cases (incomplete entities, hiding, etc.)
    const timeoutId = setTimeout(() => {
      // Skip if entity is still loading
      if (selectedEntity.isComplete === false) {
        return;
      }

      // Skip timeout callback if user has manually closed drawer
      if (mediaDrawerManuallyClosedByUser) {
        return;
      }

      const currentHasMedia = displayMediaItems?.length > 0;

      // MediaDrawer now works in both compact and fullscreen modes

      // Auto-hide when entity has no media
      if (!currentHasMedia && isMediaDrawerVisible) {
        setMediaDrawerVisibility(false);
      }
      // Respect manual close - hide drawer if user manually closed it
      else if (mediaDrawerManuallyClosedByUser && isMediaDrawerVisible) {
        setMediaDrawerVisibility(false);
      }
    }, 100); // Small delay to let state settle

    // Store the timeout reference
    timeoutRef.current = timeoutId;

    return () => {
      clearTimeout(timeoutId);
      if (timeoutRef.current === timeoutId) {
        timeoutRef.current = null;
      }
    };
  }, [
    selectedEntity?.id,
    selectedEntity?.isComplete, // Include isComplete to react to data loading completion
    displayMediaItems?.length,
    setMediaDrawerVisibility,
    setMediaDrawerManualCloseByUser,
    mediaDrawerManuallyClosedByUser
  ]);

  // Reset manual flags when entity changes to allow auto-show for new entities
  useEffect(() => {
    const currentEntityId = selectedEntity?.id;
    const prevEntityId = entityIdRef.current;

    if (currentEntityId && currentEntityId !== prevEntityId) {
      // Reset manual flag when entity changes
      setMediaDrawerManualCloseByUser(false);
      // Reset drawer to compact layout for each new entity so users aren't
      // dropped into a fullscreen grid they didn't ask for. They can re-expand
      // via the top-right toggle.
      setDataDrawerMode("compact");
      entityIdRef.current = currentEntityId;
    }
  }, [selectedEntity?.id, selectedEntity?.name, setMediaDrawerManualCloseByUser, setDataDrawerMode]);

  // MediaDrawer now just consumes data from SelectionContext - no need to fetch data here
  // SelectionContext automatically handles complete data fetching when entities are selected

  type CarouselNavButtonProps = IconButtonProps;
  function CarouselNavButton({ onClick, children, sx, ...rest }: CarouselNavButtonProps) {
    return (
      <IconButton
        onClick={onClick}
        sx={{
          position: "absolute",
          bgcolor: "primary.main",
          color: "common.white",
          "&:hover": {
            bgcolor: "grey.600",
            color: "secondary.main"
          },
          ...sx
        }}
        {...rest}
      >
        {children}
      </IconButton>
    );
  }
  const CustomLeftArrow = ({ onClick }: { onClick?: () => void }) => (
    <CarouselNavButton onClick={onClick} sx={{ left: 0 }}>
      <ChevronLeft
        sx={{
          fontSize: "2rem"
        }}
      />
    </CarouselNavButton>
  );

  const CustomRightArrow = ({ onClick }: { onClick?: () => void }) => (
    <CarouselNavButton onClick={onClick} sx={{ right: 0 }}>
      <ChevronRight
        sx={{
          fontSize: "2rem"
        }}
      />
    </CarouselNavButton>
  );

  const handleItemClick = useCallback(
    async (item: MediaItemDetailed) => {
      console.log("[MediaDrawer] Clicked media item:", {
        id: item.id,
        name: item.name,
        url: item.url,
        thumbnail: item.thumbnail,
        media_type: item.media_type,
        hasRemoteDetails: !!item.remote_details,
        remoteDetailsUrl: item.remote_details?.url,
        originalFile: item.original_file,
        dataSourceType: getDataSourceType(item),
        hasFulcrumPhoto: !!item.fulcrum_photo
      });

      setSelectedItem(item);

      // Use setTimeout to ensure the previous dialog is fully closed before opening new one
      setTimeout(async () => {
        try {
          startLoading();

          // Combine item with adapter-provided URLs
          const previewItem = await fetchMediaUrlAndThumbnail(item, profile);

          // Don't update list if item is already up-to-date
          const shouldUpdateList = previewItem?.url !== item?.url;

          if (shouldUpdateList) {
            const updatedList = updateMediaList(items || [], previewItem);
            setMediaItems(updatedList);
          }

          // Update selected item and open the carousel (Layer B overlay)
          setSelectedItem(previewItem);

          // openMediaDetail sets isMediaDetailOpen=true + stores selected item.
          // This is independent of any primary popup (Layer A / popupStore),
          // so geo-entity or non-geo-entity popups remain open alongside the carousel.
          openMediaDetail(previewItem);
        } catch (error) {
          console.error("[MediaDrawer] ❌ Failed to prepare item for preview:", error);

          // Final fallback: use original item as-is
          console.log("[MediaDrawer] 🔄 Using original item as final fallback");
        } finally {
          stopLoading();
        }
      }, 50);
    },
    [items, openMediaDetail]
  );

  // Helper function to check if a media item can be selected as a standalone entity
  const canSelectAsEntity = useCallback((item: MediaItemDetailed): boolean => {
    const dataSourceType = getDataSourceType(item);

    // For now, only Django media items can be standalone entities
    // Fulcrum photos are just attachments to records
    return dataSourceType === "django";
  }, []);

  // New function to handle selecting a media item as the selected entity
  const handleSelectAsEntity = useCallback(
    (item: MediaItemDetailed, event: React.MouseEvent) => {
      event.stopPropagation(); // Prevent the preview from opening

      // Note: This function should only be called for items that canSelectAsEntity() returns true
      // The button is conditionally rendered, so we shouldn't reach here for non-standalone items
      const dataSourceType = getDataSourceType(item);

      // Convert MediaItemDetailed to Entity interface
      // CRITICAL: Set entityType to 'media' so SelectionContext finds the correct media endpoint
      const entityFromMedia = {
        id: item.id,
        name: item.name,
        entityType: "media", // Always use 'media' so SelectionContext finds the media endpoint
        coordinates: item.coordinates ? ([item.coordinates[0], item.coordinates[1]] as [number, number]) : undefined,
        geometry: item.geometry || undefined,
        properties: {
          ...item,
          fromMediaDrawer: true, // Mark source for debugging
          parentEntityId: selectedEntity?.id, // Track the parent entity
          parentEntityType: selectedEntity?.entityType, // Track the parent entity type
          parentEntityName: selectedEntity?.name // Track the parent entity name
        },
        relatedMedia: [item], // Include itself as related media
        hasMedia: true,
        // Django media items can be fetched independently, others are already complete
        isComplete: dataSourceType !== "django", // Django media can be fetched, others are complete
        relationships:
          dataSourceType === "django"
            ? undefined
            : {
                related_entities: {},
                geo_entities: {},
                media: [] // Non-Django media items don't have relationships
              }
      };

      console.log("🔴 [MediaDrawer] Selecting media item as entity:", {
        id: item.id,
        name: item.name,
        entityType: "media", // This should trigger SelectionContext to find the media endpoint
        parentEntityId: selectedEntity?.id,
        parentEntityType: selectedEntity?.entityType,
        previousEndpoint: selectedEndpoint?.name
      });

      // Find the Media endpoint from config and pass it explicitly
      const mediaEndpoint = config.apiEndpoints.find((ep) => ep.entityType === "media");

      console.log("🔧 [MediaDrawer] Setting media entity with explicit endpoint:", {
        entityId: entityFromMedia.id,
        entityType: entityFromMedia.entityType,
        mediaEndpointFound: !!mediaEndpoint,
        mediaEndpointName: mediaEndpoint?.name
      });

      // Use SelectionContext to set this as the selected entity with the correct endpoint
      // setSelectedEntity(entityFromMedia, mediaEndpoint);
      setSelectedPrimaryStates({
        selectedEntityId: entityFromMedia.id,
        selectedEndpoint: mediaEndpoint
      });

      // Route the popup based on the media's geometry, mirroring SideBar.handleSelectEntity.
      // Without this the popupType stays whatever it was before (e.g. "geo" from the parent
      // entity), so non-geo media incorrectly opened as a geo popup (popup=geo) instead of
      // the media viewer. Geotagged media → geo popup on the map; non-geo media → media viewer.
      if (hasValidGeometry(entityFromMedia as Entity)) {
        openGeoPopup();
      } else if (entityIsMedia(entityFromMedia as Entity)) {
        openMediaViewer();
      } else {
        openNonGeoPopup();
      }
    },
    [selectedEntity, selectedEndpoint, config, openGeoPopup, openMediaViewer, openNonGeoPopup, setSelectedPrimaryStates]
  );

  // Show toggle button when entity is selected
  if (!shouldShowToggleButton) {
    return null;
  }

  // If displayMediaItems is empty don't render drawer
  if ((displayMediaItems && !displayMediaItems?.length) || !mediaDrawerRelationshipGate) return null;

  return (
    <>
      {/* Toggle Button: Now relative, positioned at map bottom (no fixed/absolute).
          Hidden in fullscreen mode because (a) its `bottom: 160` calc reserves space
          for the compact strip and looks misplaced when the drawer fills the
          viewport; (b) the in-drawer top-right toolbar already exposes a
          FullscreenExit button that returns the user to compact, from which they
          can hide the drawer via this assembly's chevron. */}
      {!isDrawerExpanded && (
        <Box
          id='media_drawer'
          sx={{
            // Inside the !isDrawerExpanded branch, dataDrawerMode is always
            // "hidden" or "compact" — never "fullscreen" — so the position /
            // zIndex are constant rather than mode-dependent.
            position: "absolute",
            bottom: isMediaDrawerVisible ? 160 : 60,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: Z_INDEX_TOGGLE_COMPACT,
            transition: "bottom 0.3s ease-in-out",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            gap: 1
          }}
        >
          {isMediaDrawerVisible && (
            <Typography
              variant='caption'
              sx={{
                color: "white",
                textAlign: "left",
                backgroundColor: "grey.800",
                padding: "4px 8px",
                borderRadius: "4px",
                position: "absolute",
                left: "1rem",
                bottom: "-3.25rem"
              }}
            >
              {displayMediaItems?.length < MAX_ITEMS
                ? `Showing ${displayMediaItems.length} items`
                : `Showing first ${MAX_ITEMS} of ${displayMediaItems?.length} items`}
            </Typography>
          )}
          {displayMediaItems?.length > 0 && (
            <ToggleButton
              isOpen={isMediaDrawerVisible}
              onClick={toggleDrawer}
              direction='vertical'
              showIndicator={displayMediaItems.length > 0}
              reverseDirection={true}
            />
          )}
          {/* Media count indicator */}
          {displayMediaItems.length > 0 && !isMediaDrawerVisible && (
            <Typography
              variant='caption'
              sx={{
                position: "absolute",
                top: 32,
                right: "1.5rem",
                backgroundColor: "primary.main",
                color: "white",
                borderRadius: "50%",
                minWidth: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.7rem",
                fontWeight: "bold",
                zIndex: 1
              }}
            >
              {displayMediaItems.length}
            </Typography>
          )}
        </Box>
      )}

      {/* Drawer Content - Only shown when explicitly opened */}
      <ErrorBoundary
        resetKeys={[selectedEntity?.id, displayMediaItems.length].filter((key) => key !== undefined)}
        onError={(error, errorInfo) => {
          console.error("MediaDrawer Error:", error, errorInfo);
        }}
      >
        <Box
          sx={{
            width: "100%",
            ml: 0,
            backgroundColor: "#344054",
            padding: "0.625rem 0.75rem 0.625rem 1rem",
            display: "flex",
            flexDirection: "column",
            zIndex: dataDrawerMode === "fullscreen" ? Z_INDEX_DRAWER_FULLSCREEN : Z_INDEX_DRAWER_COMPACT,
            pointerEvents: "auto",
            borderTop: "1px solid",
            borderColor: "grey.500",
            transition: "top 0.3s ease-out, height 0.3s ease-out",
            ...(isDrawerExpanded
              ? {
                  position: "fixed",
                  top: timelineOffsetPx ? `calc(${NAVBAR_HEIGHT} + ${timelineOffsetPx}px)` : NAVBAR_HEIGHT,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: "auto",
                  overflowY: "auto"
                }
              : {
                  position: "relative",
                  height: `${MEDIA_DRAWER_CONTENT_HEIGHT}px`
                }),
            ...(!isMediaDrawerVisible && {
              height: 0,
              paddingY: 0,
              top: "200px"
            })
          }}
        >
          {/* Top-right toolbar: zoom controls (fullscreen only) + expand/collapse */}
          {isMediaDrawerVisible && displayMediaItems.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                zIndex: 10,
                display: "flex",
                gap: 0.5
              }}
            >
              {isDrawerExpanded && (
                <>
                  <Tooltip title='Smaller tiles' arrow>
                    <span>
                      <IconButton
                        onClick={zoomGridOut}
                        size='small'
                        disabled={!canZoomOut}
                        aria-label='Decrease grid tile size'
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: "rgba(0, 0, 0, 0.55)",
                          color: "white",
                          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                          "&.Mui-disabled": { color: "rgba(255, 255, 255, 0.3)", backgroundColor: "rgba(0, 0, 0, 0.35)" }
                        }}
                      >
                        <ZoomOut fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Larger tiles' arrow>
                    <span>
                      <IconButton
                        onClick={zoomGridIn}
                        size='small'
                        disabled={!canZoomIn}
                        aria-label='Increase grid tile size'
                        sx={{
                          width: 28,
                          height: 28,
                          backgroundColor: "rgba(0, 0, 0, 0.55)",
                          color: "white",
                          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                          "&.Mui-disabled": { color: "rgba(255, 255, 255, 0.3)", backgroundColor: "rgba(0, 0, 0, 0.35)" }
                        }}
                      >
                        <ZoomIn fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
              <Tooltip title={isDrawerExpanded ? "Collapse media drawer" : "Expand media drawer"} arrow>
                <IconButton
                  onClick={toggleDrawerExpansion}
                  size='small'
                  aria-label={isDrawerExpanded ? "Collapse media drawer" : "Expand media drawer"}
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: "rgba(0, 0, 0, 0.55)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.8)"
                    }
                  }}
                >
                  {isDrawerExpanded ? <FullscreenExit fontSize='small' /> : <Fullscreen fontSize='small' />}
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {displayMediaItems.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                flexDirection: "column",
                gap: 2
              }}
            >
              {selectedEntity && !selectedEntity.isComplete ? (
                <>
                  <CircularProgress size={40} sx={{ color: "white" }} />
                  <Typography variant='body1' sx={{ color: "white" }}>
                    Loading media for {selectedEntity.name}...
                  </Typography>
                </>
              ) : (
                <Typography variant='body1' sx={{ color: "white" }}>
                  No media items found for the selected entity
                </Typography>
              )}
            </Box>
          ) : (
            <>
              {(() => {
                // Single tile renderer used by both compact (carousel) and fullscreen (grid) modes.
                // Layout differences are passed in via outerSx; tile contents are identical.
                // Click semantics are passed in so carousel (single-click open) and grid
                // (single-click select / double-click open, Lightroom-style) can differ.
                const renderTile = (
                  item: MediaItemDetailed,
                  outerSx: object,
                  imageHeight: string,
                  onClick: () => void,
                  onDoubleClick?: () => void
                ) => {
                  const isActive = selectedItem && `${selectedItem?.type}-${selectedItem?.id}` === `${item?.type}-${item?.id}`;
                  const thumbnailState = getThumbnailUrl(item);
                  const isIcon = thumbnailState.url.includes("/icons/");
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        position: "relative",
                        backgroundColor: "#344054",
                        border: "0.879px solid #667085",
                        borderRadius: "0.43963rem",
                        ...outerSx
                      }}
                    >
                      <Card
                        aria-current={isActive}
                        sx={{
                          height: "100%",
                          width: "100%",
                          cursor: "pointer",
                          "&:hover": {
                            background: theme.palette?.primary?.main,
                            color: theme.palette?.secondary?.main,
                            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)"
                          },
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                          backgroundColor: "#344054",
                          "&[aria-current='true']": {
                            backgroundColor: "primary.main"
                          }
                        }}
                        onClick={onClick}
                        onDoubleClick={onDoubleClick}
                      >
                        {/* Main image area */}
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            width: "100%",
                            alignItems: isIcon ? "center" : "flex-start",
                            justifyContent: "center",
                            position: "relative",
                            overflow: "hidden",
                            padding: isIcon ? 1 : 0
                          }}
                        >
                          {thumbnailState.loading ? (
                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(255, 255, 255, 0.1)"
                              }}
                            >
                              <CircularProgress size={30} sx={{ color: "white" }} />
                            </Box>
                          ) : (
                            <Image
                              src={thumbnailState.url}
                              alt={item.name}
                              height={imageHeight}
                              width={"100%"}
                              onError={async (e) => {
                                const target = e.target as HTMLImageElement;
                                const fallbackIcon = getThumbnailIcon(item.url || item.name);
                                if (target.src !== fallbackIcon) {
                                  console.log("[MediaDrawer] Image load failed, using fallback icon:", {
                                    itemId: item.id,
                                    originalSrc: thumbnailState.url,
                                    fallbackIcon
                                  });
                                  target.src = fallbackIcon;
                                  target.style.width = "100%";
                                  target.style.height = "100%";
                                  target.style.objectFit = "contain";
                                  target.style.background = "#344054";
                                }
                              }}
                              sx={{
                                objectFit: "cover",
                                opacity: thumbnailState.error ? 0.7 : 1
                              }}
                              loading='lazy'
                            />
                          )}

                          {/* Selection button - only show for media items that can be standalone entities */}
                          {canSelectAsEntity(item) && (
                            <Tooltip title='Select as main entity' arrow>
                              <IconButton
                                onClick={(e) => handleSelectAsEntity(item, e)}
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                                  color: "white",
                                  width: 28,
                                  height: 28,
                                  "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.9)",
                                    transform: "scale(1.1)"
                                  },
                                  transition: "all 0.2s ease"
                                }}
                                size='small'
                              >
                                <LocationOnIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>

                        {/* Title and description area - compact with centered text */}
                        <Box
                          sx={{
                            padding: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 0.25,
                            height: "2rem",
                            flexShrink: 0
                          }}
                        >
                          <Typography
                            variant='body2'
                            sx={{
                              textAlign: "center",
                              color: "#FCFCFD",
                              fontSize: "0.875rem",
                              fontWeight: 400,
                              fontStyle: "normal",
                              lineHeight: "normal",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: "vertical",
                              mb: 0,
                              width: "100%",
                              letterSpacing: "-0.0175rem"
                            }}
                          >
                            {item.name}
                          </Typography>
                        </Box>
                      </Card>
                    </Box>
                  );
                };

                if (isDrawerExpanded) {
                  // Fullscreen grid: responsive CSS grid that auto-fills available width.
                  // Tiles are square via aspect-ratio, scrollable list when content exceeds viewport.
                  // Lightroom-style interaction: single-click selects, double-click opens preview.
                  return (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(auto-fill, minmax(${gridTileSize}px, 1fr))`,
                        gap: "0.75rem",
                        paddingTop: "0.75rem",
                        paddingBottom: "0.75rem",
                        // Reserve space for the toolbar (zoom + expand) at top-right
                        paddingRight: "8rem",
                        alignContent: "start"
                      }}
                    >
                      {limitedItems.map((item) =>
                        renderTile(
                          item,
                          {
                            width: "100%",
                            aspectRatio: "1"
                          },
                          "100%",
                          () => setSelectedItem(item),
                          () => handleItemClick(item)
                        )
                      )}
                    </Box>
                  );
                }

                // Compact: existing horizontal carousel.
                return (
                  <Box
                    sx={{
                      flex: 1,
                      height: "100%",
                      flexDirection: "column",
                      "& .react-multi-carousel-list": {
                        margin: 0,
                        padding: 0
                      },
                      "& .react-multi-carousel-track": {
                        margin: 0,
                        padding: 0,
                        gap: "0.62rem"
                      },
                      "& .react-multi-carousel-item": {
                        margin: 0,
                        padding: 0,
                        width: "6.25rem !important"
                      },
                      marginBottom: 0
                    }}
                  >
                    {shouldCenterCompactItems ? (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          gap: "0.62rem"
                        }}
                      >
                        {limitedItems.map((item) =>
                          renderTile(
                            item,
                            {
                              width: "6.25rem",
                              height: "6.25rem"
                            },
                            "4.275rem",
                            () => handleItemClick(item)
                          )
                        )}
                      </Box>
                    ) : (
                      <Carousel
                        responsive={responsive}
                        customLeftArrow={<CustomLeftArrow />}
                        customRightArrow={<CustomRightArrow />}
                        itemClass='carousel-item'
                        centerMode
                        infinite={false}
                        removeArrowOnDeviceType={["mobile"]}
                        ssr={false}
                        partialVisible={false}
                      >
                        {limitedItems.map((item) =>
                          renderTile(
                            item,
                            {
                              width: "6.25rem",
                              height: "6.25rem"
                            },
                            "4.275rem",
                            () => handleItemClick(item)
                          )
                        )}
                      </Carousel>
                    )}
                  </Box>
                );
              })()}

              {SHOW_SEARCH_BAR && (
                <Box
                  sx={{
                    mt: "auto",
                    px: 2,
                    pb: 1
                  }}
                >
                  <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder='Search by name, description or type...' />
                </Box>
              )}
            </>
          )}
        </Box>
      </ErrorBoundary>
    </>
  );
};

export default MediaDrawer;
